<template>
  <div class="pa-3 ma-3">
    <v-alert-dialog ref="alert"></v-alert-dialog>

    <!-- Crear / Editar -->
    <v-dialog max-width="65%" v-model="dialogAddEdit" persistent>
      <v-card>
        <v-card-title>
          {{ isCreate ? "Crear" : "Editar" }} Patrocinador
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6" class="mb-0 pb-0">
              <v-text-field
                v-model="editedItem.nombre_corto"
                label="Nombre corto"
                outlined
                dense
                counter="64"
              />
            </v-col>

            <v-col cols="12" md="6" class="mb-0 pb-0">
              <v-text-field
                v-model="editedItem.nombre_completo"
                label="Nombre completo"
                outlined
                dense
                counter="256"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-tooltip bottom :max-width="340" color="black">
                <template v-slot:activator="{ on, attrs }">
                  <span class="d-inline-flex align-center" v-bind="attrs" v-on="on">
                    <v-switch
                      v-model="editedItem.activo"
                      label="Activo"
                      hide-details
                      class="mt-0"
                    />
                  </span>
                </template>
                <div class="pa-2">
                  Si <strong>está desactivado</strong>, la cuenta <strong>no podrá iniciar sesión</strong>
                  aunque el usuario y la contraseña sean correctos.
                </div>
              </v-tooltip>
            </v-col>

            <!-- Detalles de Mastografía -->
            <v-col cols="12" md="4">
              <v-tooltip bottom :max-width="360" color="black">
                <template v-slot:activator="{ on, attrs }">
                  <span class="d-inline-flex align-center" v-bind="attrs" v-on="on">
                    <v-switch
                      v-model="editedItem.detalles_mastografia"
                      label="Detalles de Mastografía"
                      hide-details
                      class="mt-0"
                    />
                  </span>
                </template>
                <div class="pa-2">
                  Muestra información <strong>anonimizada</strong> (edad, sexo y lugar de atención)
                  y permite <strong>descargar DICOM (DCM) anonimizados</strong> para este patrocinador.
                </div>
              </v-tooltip>
            </v-col>

            <!-- Acceso Total -->
            <v-col cols="12" md="4">
              <v-tooltip bottom :max-width="360" color="black">
                <template v-slot:activator="{ on, attrs }">
                  <span class="d-inline-flex align-center" v-bind="attrs" v-on="on">
                    <v-switch
                      v-model="editedItem.acceso_total"
                      label="Acceso Total"
                      hide-details
                      class="mt-0"
                    />
                  </span>
                </template>
                <div class="pa-2">
                  Permite ver <strong>todos los proyectos</strong> de la fundación, sin restringir
                  solo a los proyectos del patrocinador. Pensado para <strong>cuentas internas (UBGA)</strong>.
                  <strong class="red--text">Úsar con cautela.</strong>
                </div>
              </v-tooltip>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="editedItem.usuario"
                label="Usuario (opcional)"
                outlined
                clearable
                dense
                counter="64"
              />
            </v-col>

            <v-col cols="12" md="6">
              <!-- Contraseña con ojo y candado alineados -->
              <v-text-field
                v-model="editedItem.password"
                :type="showPass ? 'text' : 'password'"
                label="Contraseña (opcional)"
                outlined
                dense
                :readonly="!allowEditPassword"
                :clearable="allowEditPassword && !!editedItem.password"
                :append-icon="showPass ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="onClickEye"
                :append-outer-icon="!isCreate && hasPassword ? (allowEditPassword ? 'mdi-lock-open-variant' : 'mdi-lock') : undefined"
                @click:append-outer="toggleUnlockPassword"
                counter="128"
                hint="Si lo dejas vacío no se modificará."
                persistent-hint
                :loading="loadingPassword"
              />
            </v-col>

            <!-- Archivos -->
            <v-col cols="12">
              <v-file-input
                v-model="editedItem.logotipo"
                label="Logotipo (opcional)"
                prepend-icon="mdi-paperclip"
                outlined
                show-size
                accept="image/png, image/jpeg"
                chips
                dense
                :multiple="false"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" text @click="closeDialogAddEdit">Cancelar</v-btn>
          <v-btn color="red" text :loading="loadingSave" @click="saveDialogAddEdit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Eliminar -->
    <v-dialog max-width="65%" v-model="dialogDelete" persistent>
      <v-card>
        <v-card-title>Eliminar Patrocinador</v-card-title>
        <v-card-text>
          ¿Está seguro de que desea eliminar al patrocinador
          "<strong>{{ editedItem.nombre_completo }}</strong>"?
          Esta acción es permanente y no se puede revertir.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" text @click="closeDialogDelete">Cancelar</v-btn>
          <v-btn color="red" text :loading="loadingDelete" @click="saveDialogDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Toolbar -->
    <v-row>
      <v-col cols="12">
        <v-btn color="primary" @click="openDialogAddEdit(null)">
          Crear Patrocinador
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabla -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :items="items"
          :headers="headers"
          :loading="loading"
          :items-per-page="10"
        >
          <template v-slot:[`item.logotipo`]="{ item }">
            <v-img
              v-if="item.logotipo != null"
              :src="`/api/Catalogos/ObtenerLogotipoPatrocinador/${item.logotipo}?jwt=${token}`"
              height="80px"
              max-width="150px"
              contain
            >
            </v-img>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <v-icon small class="mr-2" @click="openDialogAddEdit(item)">
              mdi-pencil
            </v-icon>
            <v-icon small class="mr-2" @click="openDialogDelete(item)">
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import VAlertDialog from '@/components/VAlertDialog.vue';

export default {
  components: { VAlertDialog },
  data: () => ({
    dialogAddEdit: false,
    dialogDelete: false,
    loading: false,
    loadingSave: false,
    loadingDelete: false,

    // Estado de password
    showPass: false,
    hasPassword: false,        // viene del backend por ítem (en edición)
    allowEditPassword: true,   // usuario desbloqueó para editar
    loadingPassword: false,

    items: [],
    headers: [
      { text: "Logotipo", value: "logotipo", align: "center", width: "15%" },  
      { text: "Nombre corto", value: "nombre_corto", align: "center", width: "25%" },
      { text: "Nombre completo", value: "nombre_completo", align: "center", width: "25%"},
      { text: "Número de Proyectos", value: "num_proyectos", align: "center", width: "25%"},
      { text: "Acciones", value: "actions", align: "center", width: "10%" }
    ],
    editedItem: {
      id_patrocinador: null,
      nombre_corto: "",
      nombre_completo: "",
      usuario: null,
      password: null,
      logotipo: null,
      activo: true,
      acceso_total: false,
      detalles_mastografia: false,
      has_password: false, // <- esperado del backend
    },
    defaultEditedItem: {
      id_patrocinador: null,
      nombre_corto: "",
      nombre_completo: "",
      usuario: null,
      password: null,
      logotipo: null,
      activo: true,
      acceso_total: false,
      detalles_mastografia: false,
      has_password: false,
    },
  }),
  mounted() {
    this.getItems();
  },
  computed: {
    ...mapState(["token"]),
    isCreate(){
      return this.editedItem.id_patrocinador === null;
    }
  },
  methods: {
    // Listado
    getItems() {
      this.loading = true;
      axios
        .get('/Catalogos/ListarPatrocinadores')
        .then(response => { this.items = response.data; })
        .catch(() => {
          this.$refs.alert.setMessage("", "No se pudo obtener la lista de patrocinadores.");
          this.$refs.alert.open();
        })
        .finally(() => (this.loading = false));
    },

    // Crear/Editar - abrir/cerrar
    openDialogAddEdit(editItem = null) {
      // Reset estado de password
      this.showPass = false;

      if (editItem != null) {
        const hasPwd = !!editItem.has_password;
        this.editedItem = Object.assign({}, editItem, {
          logotipo: null,
          has_password: hasPwd,
          password: hasPwd ? '********' : null,
        });

        this.hasPassword = hasPwd;
        this.allowEditPassword = !hasPwd; // si tiene password → bloqueado (evita edición accidental)
      } else {
        this.editedItem = Object.assign({}, this.defaultEditedItem);
        this.hasPassword = false;
        this.allowEditPassword = true; // crear → editable
      }

      this.dialogAddEdit = true;
    },

    closeDialogAddEdit() {
      this.editedItem = Object.assign({}, this.defaultEditedItem);
      this.dialogAddEdit = false;
    },

    // Eliminar
    openDialogDelete(editItem) {
      this.editedItem = Object.assign({}, editItem);
      this.dialogDelete = true;
    },
    closeDialogDelete() {
      this.editedItem = Object.assign({}, this.defaultEditedItem);
      this.dialogDelete = false;
    },
    saveDialogDelete() {
      this.loadingDelete = true;
      axios
        .delete(`/Catalogos/EliminarPatrocinador/${this.editedItem.id_patrocinador}`)
        .then(() => {
          this.closeDialogDelete();
          this.getItems();
        })
        .catch(() => {
          this.$refs.alert.setMessage("", "No se pudo eliminar el patrocinador.");
          this.$refs.alert.open();
        })
        .finally(() => (this.loadingDelete = false));
    },

    // Ojo (revelar/ocultar) — trae la real 1 sola vez si está enmascarada
    onClickEye() {
      // Crear o edición sin pass previa → solo alterna visibilidad
      if (this.isCreate || !this.hasPassword) {
        this.showPass = !this.showPass;
        return;
      }

      // Edición con pass existente
      if (!this.showPass) {
        // Revelar por primera vez
        this.loadingPassword = true;
        axios
        .get(`/Catalogos/ObtenerPasswordPatrocinador/${this.editedItem.id_patrocinador}`)
        .then(r => {
          this.editedItem.password = r.data.password || '';
          this.showPass = true;
        })
        .catch(() => {
          this.$refs.alert.setMessage("", "No se pudo obtener la contraseña.");
          this.$refs.alert.open();
        })
        .finally(() => { this.loadingPassword = false; });
      } else {
        // Ocultar
        this.showPass = false;
        if (!this.allowEditPassword) {
          // si sigue bloqueado, volvemos a la máscara visual
          this.editedItem.password = '********';
        }
      }
    },

    // Candado (bloquear/desbloquear) — edición consciente
    toggleUnlockPassword() {
      if (this.isCreate || !this.hasPassword) return; // no aplica

      this.allowEditPassword = !this.allowEditPassword;

      if (!this.allowEditPassword) {
        // al bloquear: ocultar y re-mascarar
        this.showPass = false;
        this.editedItem.password = '********';
      } else {
        // al desbloquear: si no la trajimos aún, tráela; si ya la tenemos, muéstrala
        this.onClickEye();
      }
    },

    // Crear/Editar - guardar (FormData)
    saveDialogAddEdit() {
      const corto   = this.editedItem.nombre_corto ? this.editedItem.nombre_corto.trim() : "";
      const nombre  = this.editedItem.nombre_completo ? this.editedItem.nombre_completo.trim() : "";
      const usuario = this.editedItem.usuario ? this.editedItem.usuario.trim() : "";
      let   pwd     = this.editedItem.password || null;
      const activo = this.editedItem.activo;
      const acceso_total = this.editedItem.acceso_total;
      const detalles_mastografia = this.editedItem.detalles_mastografia;

      // Validaciones simples
      if (!corto || corto.length < 4 || corto.length > 128) {
        this.$refs.alert.setMessage("", "Ingrese un Nombre corto entre 4 y 128 caracteres.");
        this.$refs.alert.open();
        return;
      }
      if (!nombre || nombre.length < 4 || nombre.length > 1024) {
        this.$refs.alert.setMessage("", "Ingrese un Nombre completo entre 4 y 1024 caracteres.");
        this.$refs.alert.open();
        return;
      }
      if (usuario && (usuario.length < 4 || usuario.length > 32)) {
        this.$refs.alert.setMessage("", "Ingrese un Usuario entre 4 y 32 caracteres.");
        this.$refs.alert.open();
        return;
      }

      // Reemplazar "********" por null para NO enviar ni validar
      if (pwd === '********') {
        pwd = null;
      }

      // ¿Se intentará enviar password?
      const willSendPwd = (this.isCreate || this.allowEditPassword) && !!pwd;

      // Validar password solo si realmente se enviará
      if (willSendPwd && (pwd.length < 8 || pwd.length > 32)) {
        this.$refs.alert.setMessage("", "Ingrese una Contraseña entre 8 y 32 caracteres.");
        this.$refs.alert.open();
        return;
      }

      this.loadingSave = true;

      const formData = new FormData();
      formData.append('nombre_corto', corto);
      formData.append('nombre_completo', nombre);
      formData.append('activo', activo);
      formData.append('detalles_mastografia', detalles_mastografia);
      formData.append('acceso_total', acceso_total);

      if (usuario) formData.append('usuario', usuario);

      // Opcional: reforzar edición consciente en backend
      // formData.append('change_password', willSendPwd ? 'true' : 'false');

      if (willSendPwd) formData.append('password', pwd);
      if (this.editedItem.logotipo) formData.append('logotipo', this.editedItem.logotipo);

      axios({
        method: this.isCreate ? 'POST' : 'PUT',
        url: this.isCreate
          ? '/Catalogos/CrearPatrocinador'
          : `/Catalogos/EditarPatrocinador/${this.editedItem.id_patrocinador}`,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      })
        .then(() => {
          this.getItems();
          this.closeDialogAddEdit();
        })
        .catch(error => {
          if (error.response && error.response.status === 409) {
            this.$refs.alert.setMessage("", "Existe un Patrocinador con el mismo Nombre o Usuario.");
            this.$refs.alert.open();
          } else {
            this.$refs.alert.setMessage("", "No se pudo guardar el patrocinador.");
            this.$refs.alert.open();
          }
        })
        .finally(() => (this.loadingSave = false));
    },
  }
}
</script>